import AddIcon from '!svg-react-loader?!images-react-svg/icons/icon-add.svg';

import React from 'react';

import propTypes from 'prop-types';
import accountPropTypes from 'apps/account/propTypes';
import Button from 'common/components/Button';
import * as ZipcodeFormatter from 'common/formatters/ZipCode';
import { getCountryNameFromISO } from 'common/utils';
import {
  IntlMessageRenderProp,
  messageGenerator,
} from 'common/components/Intl';
import LocationSearchInput from 'common/components/LocationSearchInput';
import {
  Form,
  Field,
  Input,
  Textarea,
  Select,
  Submit,
  TranslatedField,
  ZipCode,
} from 'common/components/Form';
import * as m from './messages';
import { CountryPhoneInput } from 'common/components/Form/CountryPhoneInput';

export default class BaseAddressForm extends React.Component {
  static propTypes = {
    addressType: accountPropTypes.AddressType.isRequired,
    deleteAddress: propTypes.func,
    handleSubmit: propTypes.func.isRequired,
    initialValues: accountPropTypes.Address,
    formText: propTypes.string,
    fieldProps: propTypes.object,
    onSubmit: propTypes.func.isRequired,
    stateOptions: propTypes.array,
    enableLocationSearch: propTypes.bool,
  };

  static defaultProps = {
    fieldProps: {},
    enableLocationSearch: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      isSubmitting: false,
      isDeleting: false,
      showFields: {},
      selectedState: {},
      isDefaultBilling: this.props.fieldProps.default_billing,
      isNameLocked:
        this.props.fieldProps.firstName || this.props.fieldProps.lastName,
      messages: [
        ...messageGenerator(m),
        {
          id: getCountryNameFromISO(this.props.initialValues.country),
          name: 'country',
        },
      ],
    };
  }

  componentWillUnmount() {
    this.onSubmitCancelable && this.onSubmitCancelable.cancel();
  }

  // The onClick behaviour for the field adder buttons.
  // When the field is added to showFields,
  // the component will render the field as a form input.
  setShowField = field => {
    this.setState(prevState => ({
      showFields: { ...prevState.showFields, [field]: true },
    }));
  };

  renderFieldAdder = (field, label) => {
    return (
      <a
        className="AddressForm__company-container"
        onClick={() => this.setShowField(field)}
      >
        <span className="AddressForm__text">{label}</span>
        <AddIcon className="AddressForm__add-icon" />
      </a>
    );
  };

  renderSpecialInstructions = ({ raw, className, component }) => {
    // Only render a conditional field if the value of the field is truthy
    // or we've explicitly set the field to be shown via state.showFields
    const { initialValues } = this.props;
    if (
      (initialValues && initialValues.special_instructions) ||
      this.state.showFields.special_instructions
    )
      return (
        <Field
          name="special_instructions"
          type="text"
          className={className}
          component={Textarea}
          label={raw}
          {...this.props.fieldProps.special_instructions}
        />
      );
    // Render the adder (+) as long as the field isn't disabled - as there is
    // no point providing an adder to show an empty field that can't be edited
    // because it is disabled.
    const fieldProp = this.props.fieldProps.special_instructions;
    if (fieldProp && fieldProp.disabled) return null;
    return this.renderFieldAdder('special_instructions', component);
  };

  renderCompanyField = ({ raw, className, component }) => {
    // Only render a conditional field if the value of the field is truthy
    // or we've explicitly set the field to be shown via state.showFields
    const { initialValues } = this.props;
    if (
      (initialValues && initialValues.company) ||
      this.state.showFields.company
    )
      return (
        <Field
          name="company"
          type="text"
          component={Input}
          className={className}
          label={raw}
          {...this.props.fieldProps.company}
        />
      );
    // Render the adder (+) as long as the field isn't disabled - as there is
    // no point providing an adder to show an empty field that can't be edited
    // because it is disabled.
    const fieldProp = this.props.fieldProps.company;
    if (fieldProp && fieldProp.disabled) return null;
    return this.renderFieldAdder('company', component);
  };

  onAddressSelect = address => {
    Object.keys(address).forEach(key => {
      this.props.change(key, address[key]);
      if (key === 'state') {
        this.updateFormStateValue(address[key]);
      }
    });
  };

  updateFormStateValue = selectedState => {
    this.setState({ selectedState });
  };

  handleSubmit = values => {
    this.setState({ isSubmitting: true });
    values.zipcode = ZipcodeFormatter.formatZipcode(
      values.zipcode,
      values.country
    );
    this.onSubmitCancelable = this.props.onSubmit(
      values,
      this.props.addressType
    );

    return this.onSubmitCancelable.promise.then(undefined, error => {
      if (error.isCanceled) {
        return;
      }

      this.setState({ isSubmitting: false });
    });
  };

  handleDeleteClick = () => {
    return new Promise((resolve, reject) => {
      this.setState({ isDeleting: true });
      this.props.deleteAddress(this.props.initialValues.id, resolve, reject);
    })
      .then(() => this.setState({ isDeleting: false }))
      .catch(() => this.setState({ isDeleting: false }));
  };

  renderStateField() {
    if (this.props.stateOptions && this.props.stateOptions.length > 0) {
      return (
        <TranslatedField
          name="state"
          component={Select}
          options={this.props.stateOptions}
          placeholder={m.STATE}
          noResultsText={m.NO_SUCH_STATE}
          currentValue={this.state.selectedState}
          {...this.props.fieldProps.state}
        />
      );
    }

    return null;
  }

  renderPhoneField({ raw: phoneLabel, className }) {
    const { fieldProps } = this.props;
    if (fieldProps.phone && fieldProps.phone.hidden) {
      return null;
    }

    return (
      <Field
        name="phone"
        type="phone"
        component={CountryPhoneInput}
        label={phoneLabel}
        className={className}
        {...fieldProps.phone}
      />
    );
  }

  renderSubmitButton({ component: buttonTextComponent }) {
    const { submitButtonText } = this.props;
    return (
      <Submit isSubmitting={this.state.isSubmitting}>
        {submitButtonText ? submitButtonText : buttonTextComponent}
      </Submit>
    );
  }

  formatZipcode = value => {
    return ZipcodeFormatter.formatZipcode(
      value,
      this.props.initialValues.country
    );
  };

  render() {
    const { fieldProps } = this.props;

    return (
      <IntlMessageRenderProp messages={this.state.messages}>
        {translations => (
          <Form onSubmit={this.props.handleSubmit(this.handleSubmit)}>
            <div className="AddressForm__locked-container">
              {this.state.isDefaultBilling && (
                <p
                  className={`AddressForm__text ${
                    translations[m.ADDRESS_FORM_DEFAULT].className
                  }`}
                >
                  {translations[m.ADDRESS_FORM_DEFAULT].component}
                </p>
              )}
              {this.state.isNameLocked && (
                <p
                  className={`AddressForm__text ${
                    translations[m.ADDRESS_FORM_NAMELOCKED].className
                  }`}
                >
                  {translations[m.ADDRESS_FORM_NAMELOCKED].component}
                </p>
              )}
              {this.props.formText && (
                <p className="AddressForm__text">{this.props.formText}</p>
              )}
            </div>
            <Field
              name="first_name"
              type="text"
              component={Input}
              label={translations[m.ADDRESS_FORM_FIRST_NAME].raw}
              className={translations[m.ADDRESS_FORM_FIRST_NAME].className}
              {...fieldProps.firstName}
            />
            <Field
              name="last_name"
              type="text"
              component={Input}
              label={translations[m.ADDRESS_FORM_LAST_NAME].raw}
              className={translations[m.ADDRESS_FORM_LAST_NAME].className}
              {...fieldProps.lastName}
            />
            {this.renderCompanyField(translations[m.ADDRESS_FORM_COMPANY])}

            {this.props.enableLocationSearch ? (
              <LocationSearchInput
                name="address1"
                type="text"
                component={Input}
                label={translations[m.ADDRESS_FORM_ADDRESS1].raw}
                className={translations[m.ADDRESS_FORM_ADDRESS1].className}
                initialValue={this.props.initialValues.address1}
                onAddressSelect={this.onAddressSelect}
                maxLength="35"
                {...fieldProps.address1}
              />
            ) : (
              <Field
                name="address1"
                type="text"
                component={Input}
                label={translations[m.ADDRESS_FORM_ADDRESS1].raw}
                className={translations[m.ADDRESS_FORM_ADDRESS1].className}
                maxLength="35"
                {...fieldProps.address1}
              />
            )}

            <Field
              name="address2"
              type="text"
              component={Input}
              label={translations[m.ADDRESS_FORM_ADDRESS2].raw}
              className={translations[m.ADDRESS_FORM_ADDRESS2].className}
              maxLength="35"
              {...fieldProps.address2}
            />

            <Field
              name="city"
              type="text"
              component={Input}
              label={translations[m.ADDRESS_FORM_CITY].raw}
              className={translations[m.ADDRESS_FORM_CITY].className}
              {...fieldProps.city}
            />
            {this.renderStateField()}
            <ZipCode
              label={m.ADDRESS_FORM_ZIPCODE}
              normalize={this.formatZipcode}
              {...fieldProps.zipcode}
            />
            {this.renderPhoneField(translations[m.ADDRESS_FORM_PHONE])}
            {this.renderSpecialInstructions(
              translations[m.ADDRESS_FORM_SPECIAL_INSTRUCTIONS]
            )}
            <div className="new-text AddressForm__country_label">
              {
                <span className={translations['country'].className}>
                  {translations['country'].raw}
                </span>
              }
            </div>
            <Field
              name="country"
              type="hidden"
              component={Input}
              {...fieldProps.country}
            />
            {this.renderSubmitButton(translations[m.ADDRESS_FORM_SAVE])}
            {this.props.deleteAddress ? (
              <Button
                onClick={this.handleDeleteClick}
                processing={this.state.isDeleting}
                type="button"
                className="Form__submit-button button--outline"
              >
                {translations[m.ADDRESS_FORM_DELETE].component}
              </Button>
            ) : null}
          </Form>
        )}
      </IntlMessageRenderProp>
    );
  }
}
